import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { HYDRATE } from 'next-redux-wrapper';
import { IShopInfo, IServicesShop } from '@models/Shop';
import { IActions } from '@models/Action';
import { TypePromiseInfoMapShop } from '@services/ServiceShop/TypePromise';
import { NullableType } from 'joi';

interface IState {
  shopInfo: IShopInfo;
  services: IServicesShop[];
  actions: IActions[];
  activeBlock: NonNullable<number>;
  banner: any;
  reviews: any;
  qrcode: boolean;
  hideBlockAction: boolean;
  hideBlockServices: boolean;
  newReview: number;
  infoMapData?: NullableType<TypePromiseInfoMapShop>;
}

const initialState: IState = {
  shopInfo: null,
  services: [],
  actions: [],
  activeBlock: null,
  newReview: 0,
  qrcode: false,
  hideBlockAction: false,
  hideBlockServices: false,
  banner: null,
  reviews: {
    list: [],
    activePage: 1,
    lastPage: null,
    reviewsCount: null,
    loading: true,
  },
};

export const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    setShopData: (state, action: PayloadAction<any>) => {
      state.shopInfo = action.payload?.shopInfo;
    },
    setBannerData: (state, action: PayloadAction<any>) => {
      state.banner = action.payload;
    },
    setServicesData: (state, action: PayloadAction<any>) => {
      state.services = action.payload;
      state.hideBlockServices = action.payload?.length === 0;
    },
    setActionsData: (state, action: PayloadAction<any>) => {
      state.actions = action.payload;
      state.hideBlockAction = action.payload?.length === 0;
    },
    setReviewsData: (state, action: PayloadAction<any>) => {
      state.reviews.list = action.payload.data && state.reviews.list && [...state.reviews.list, ...action.payload.data];
      state.reviews.activePage = action.payload?.current_page;
      state.reviews.lastPage = action.payload?.last_page;
      state.reviews.reviewsCount = action.payload?.total;
      state.reviews.loading = false;
    },
    setInitReviewsData: (state, action: PayloadAction<any>) => {
      state.reviews.list = action.payload?.data;
      state.reviews.activePage = action.payload?.current_page;
      state.reviews.lastPage = action.payload?.last_page;
      state.reviews.reviewsCount = action.payload?.total;
      state.reviews.loading = false;
    },
    setLoadingReviews: (state, action: PayloadAction<any>) => {
      state.reviews.loading = action.payload;
    },
    closeBanner: (state, action: PayloadAction<any>) => {
      state.banner.active = action.payload;
    },
    setActiveBlock: (state, action: PayloadAction<any>) => {
      state.activeBlock = action.payload;
    },
    setResetReviews: (state, action: PayloadAction<any>) => {
      state.reviews.list = action.payload.data;
    },
    setNewReview: (state, action: PayloadAction<any>) => {
      state.newReview = action.payload;
    },
    setPageReviews: (state, action: PayloadAction<any>) => {
      state.reviews.activePage = action.payload;
      state.reviews.loading = true;
    },
    setQRCode: (state, action: PayloadAction<any>) => {
      state.qrcode = action.payload;
    },
    setInfoMap: (state, action: PayloadAction<TypePromiseInfoMapShop>) => {
      state.infoMapData = action.payload;
    },
  },
  extraReducers: {
    [HYDRATE]: (state, action) => {
      return {
        ...state,
        ...action.payload.shop,
      };
    },
  },
});

export const {
  setShopData,
  setReviewsData,
  setInitReviewsData,
  setLoadingReviews,
  setActionsData,
  setServicesData,
  closeBanner,
  setActiveBlock,
  setBannerData,
  setResetReviews,
  setNewReview,
  setPageReviews,
  setInfoMap,
  setQRCode,
} = shopSlice.actions;

export const selectShopData = (state: RootState) => state.shop;
export const selectShopInfoData = (state: RootState) => state.shop.shopInfo;
export const selectShopReviews = (state: RootState) => state.shop.reviews;
export const selectNewReview = (state: RootState) => state.shop.newReview;

export const shopReducer = shopSlice.reducer;
